import { http } from './http.js';

export async function fetchJobs() {
  const res = await http.get('/api/redis/jobs');
  return res.data.jobs;
}

// Deleted/archived jobs (moved to MongoDB archivedJobs on delete).
export async function fetchArchivedJobs() {
  const res = await http.get('/api/redis/jobs/archived');
  return res.data.jobs || [];
}

export async function fetchJob(jobId) {
  const res = await http.get(`/api/redis/jobs/${jobId}`);
  console.log(res);
  return res.data.job;
}

export async function fetchProductionDashboardStats() {
  const { data } = await http.get('/api/redis/production-dashboard/stats');
  return data;
}

export async function fetchProductionMaterialSummary() {
  const { data } = await http.get(
    '/api/redis/production-dashboard/material-summary'
  );
  return data;
}

export async function fetchProductionMaterialTotals() {
  const { data } = await http.get(
    '/api/redis/production-dashboard/material-totals'
  );
  return data;
}

export async function fetchRemainingMaterialSummary() {
  const { data } = await http.get(
    '/api/redis/orders/remaining-material-summary'
  );
  return data;
}

export async function fetchFactoryActivity(limit = 100) {
  const { data } = await http.get(`/api/redis/factory-activity?limit=${limit}`);

  return data?.data || [];
}

export async function fetchJobActivityTimeline(jobId) {
  const { data } = await http.get(`/api/redis/jobs/${jobId}/activity-timeline`);
  return data;
}

// factory reports
export async function fetchFactoryReports(limit = 100) {
  const { data } = await http.get('/api/redis/factory-reports', {
    params: { limit }
  });
  return data;
}

// job reports timeline
export async function fetchFinalPhaseTokens() {
  const { data } = await http.get('/api/redis/production-dashboard/final-phase-tokens');
  return data;
}

export async function fetchJobReportsTimeline(jobId) {
  if (!jobId) {
    return { success: true, jobId: null, count: 0, rows: [] };
  }

  const { data } = await http.get(`/api/redis/jobs/${jobId}/reports-timeline`);
  return data;
}

/**
 * Save a job's order reservation on its own, without planning a phase.
 *
 * Moving a reservation between two jobs of the same order is the reason the
 * editor gets opened, and until this existed the edit had nowhere to go: it was
 * only ever applied as part of submitting a whole next-phase plan.
 */
export async function saveJobReservation(jobId, reservation, reason) {
  const { data } = await http.post(`/api/redis/jobs/${jobId}/reservation`, {
    reservation,
    reason
  });
  return data;
}

/**
 * Detach a job from ONE order: its metres return to stock, its reservations on
 * that order's packages are released, and the order line reads as unallocated
 * again. The job keeps its cable — it loses only the claim.
 */
export async function detachJobFromOrder(jobId, orderId, lineId = null) {
  // Without a lineId the job is released from the whole order — what the
  // wizard's "Release order" means. With one, from that line alone, which is
  // what a per-line row in project-details is offering.
  const { data } = await http.delete(
    `/api/redis/jobs/${jobId}/orders/${orderId}`,
    lineId ? { params: { lineId } } : undefined
  );
  return data;
}

/**
 * Move metres of this job from stock into an order, raising how much it may
 * reserve there. The mirror of detach; bounded by what the job has left
 * unpromised and by what the order still needs.
 */
export async function allocateJobToOrder(jobId, orderId, meters, lineId) {
  const { data } = await http.post(
    `/api/redis/jobs/${jobId}/orders/${orderId}/allocate`,
    { meters, lineId }
  );
  return data;
}

/**
 * One job's Order Context, rebuilt by the server.
 *
 * The wizard keeps this shape in local state, so a reservation made from
 * anywhere else leaves its copy behind. Asked for rather than reconstructed:
 * the shape carries the per-job budget, what each item holds and what is still
 * unpromised, and a second implementation of that would be a second thing to
 * keep in step.
 */
export async function fetchJobOrderContext(jobId) {
  const { data } = await http.get(`/api/redis/jobs/${jobId}/order-context`);
  return data;
}

/**
 * Correct the lot length of a launched product.
 *
 * The material plan is rebuilt from the recipe at the new length and the
 * stock/order split moves with it. Refused if it would take the lot below what
 * has already been promised to orders — those metres are somebody's.
 */
export async function setJobLength(jobId, length, reason) {
  const { data } = await http.post(`/api/redis/jobs/${jobId}/length`, {
    length,
    reason
  });
  return data;
}

/**
 * Finished projects, newest first, one page at a time.
 *
 * Paged in the database rather than here — this list only grows, and a factory
 * year of it has no business crossing the wire to draw a screenful. The backend
 * caps `limit` whatever is asked for.
 */
export async function fetchCompletedJobs({ page = 1, limit = 25, q = '' } = {}) {
  const { data } = await http.get('/api/redis/jobs/completed', {
    params: { page, limit, ...(q ? { q } : {}) }
  });
  return data;
}
